import { BayData, DisplayMode, createWeightedGradient } from '@amzn/ihm-floorplan-core';
import { sortBy } from 'lodash';
import { useMemo } from 'react';
import { DisplayModeOptions, defaultFillColor } from './displayModeHelper';
import getColorForText, { colorByKey, subcategoryColorByIndex } from './zoneMapColorHelper';
import { FixtureAssignment } from '../allocation/computeFixtureAllocations';
import AllocationLegend, { AllocationLegendEntry } from './AllocationLegend';
import DropZoneLegend, { DropZoneLegendEntry } from './DropZoneLegend';
import { SubcategoryAllocation } from '../types/zoneMapType';
import { useFlowLabel } from '../utils/flowLabel';

/**
 * Builds a DisplayMode that looks up a fixture's value from a Map and colors
 * the fill with `getColorForText`. Shared by Department / Fixture Type modes.
 */
function makeMapColoredMode(id: string, name: string, valueMap: Map<string, string> | undefined): DisplayMode {
  return {
    id,
    name,
    render: (fixture: BayData) => {
      const value = valueMap?.get(fixture.id ?? '') ?? '';
      return {
        displayText: value,
        fillColor: value ? getColorForText(value, true) : defaultFillColor,
      };
    },
  };
}

/**
 * Returns different displayModes and use subcategoryAllocationMode as default
 */
function useDisplayModes(
  fixtureIdToZoneDataMap: Record<
    string,
    {
      zoneName: string;
      zoneId: string;
      productTypes: string;
      isAgile: boolean;
      zoneNameKey: string;
    }
  >,
  fixtureAssignments?: FixtureAssignment[],
  allocations?: SubcategoryAllocation[],
  fixtureDepartmentMap?: Map<string, string>,
  fixtureOrderingMap?: Map<string, number>,
  fixtureTypeMap?: Map<string, string>,
  dropZoneLegendEntries?: DropZoneLegendEntry[],
): DisplayModeOptions {
  const flowLabel = useFlowLabel();

  const fixtureIdDisplayMode: DisplayMode = useMemo(
    () => ({
      id: 'FIXTURE_ID',
      name: 'Fixture Id',
      render: (fixture: BayData) => {
        const fixtureStr = fixture.id ?? '';
        const zoneData = fixtureIdToZoneDataMap[fixture.id ?? ''] || null;
        const zoneName = zoneData?.zoneName ?? '';
        const isAgile = zoneData?.isAgile ?? false;
        const zoneIndex = (zoneData as { zoneIndex?: number } | null)?.zoneIndex;
        const categoryId = (zoneData as { categoryId?: string } | null)?.categoryId;
        // GFC Drop Zone color: stable per categoryId, so view + create + read modes
        // all paint the same Drop Zone the same color regardless of list ordering.
        // Falls back to subcategoryColorByIndex for create-time WFM/Fresh editing
        // (where zoneIndex is meaningful) and finally to getColorForText for non-agile.
        const fillColor = categoryId
          ? colorByKey(categoryId)
          : zoneIndex != null && zoneName
            ? subcategoryColorByIndex(zoneIndex)
            : getColorForText(zoneName, isAgile);
        return {
          displayText: fixtureStr,
          fillColor,
        };
      },
      legend:
        dropZoneLegendEntries && dropZoneLegendEntries.length > 0
          ? DropZoneLegend({ entries: dropZoneLegendEntries })
          : undefined,
    }),
    [fixtureIdToZoneDataMap, dropZoneLegendEntries],
  );

  const fixtureModelDisplayMode: DisplayMode = useMemo(
    () => ({
      id: 'FIXTURE_MODEL',
      name: 'Fixture Model',
      render: (fixture: BayData) => ({
        displayText: fixture.model ?? '',
        fillColor: defaultFillColor,
      }),
    }),
    [],
  );

  const fixtureTypeDisplayMode: DisplayMode = useMemo(
    () => makeMapColoredMode('FIXTURE_TYPE', 'Fixture Type', fixtureTypeMap),
    [fixtureTypeMap],
  );

  const departmentMode: DisplayMode = useMemo(
    () => makeMapColoredMode('DEPARTMENT', 'Department', fixtureDepartmentMap),
    [fixtureDepartmentMap],
  );

  const sequencedColor = '#4CAF50'; // green for sequenced fixtures
  const unsequencedColor = '#E0E0E0'; // light grey for unreached fixtures

  const pickSequenceMode: DisplayMode = useMemo(
    () => ({
      id: 'PICK_SEQUENCE',
      name: flowLabel.title,
      render: (fixture: BayData) => {
        const seq = fixtureOrderingMap?.get(fixture.id ?? '');
        if (seq != null) {
          return {
            displayText: String(seq),
            fillColor: sequencedColor,
          };
        }
        return {
          displayText: '',
          fillColor: unsequencedColor,
        };
      },
    }),
    [fixtureOrderingMap, flowLabel.title],
  );

  const fixtureAssignmentMap = useMemo(() => {
    const map = new Map<string, FixtureAssignment>();
    if (fixtureAssignments) {
      for (const assignment of fixtureAssignments) {
        map.set(assignment.fixtureId, assignment);
      }
    }
    return map;
  }, [fixtureAssignments]);

  // Build legend entries for the allocation display mode
  const allocationLegendEntries: AllocationLegendEntry[] = useMemo(() => {
    const validAllocations = (allocations ?? []).filter((a) => a.subcategoryId);
    if (validAllocations.length === 0 || !fixtureAssignments || fixtureAssignments.length === 0) return [];

    // Count fixtures per subcategory
    const fixtureCounts = new Map<string, number>();
    for (const a of fixtureAssignments) {
      fixtureCounts.set(a.subcategoryId, (fixtureCounts.get(a.subcategoryId) || 0) + 1);
    }

    const isMacroLevel = validAllocations.every((a) => a.percentage === 0);

    // For macro-level (all-fixtures) mode, derive department from fixture assignments
    const subcategoryDepartment = new Map<string, string>();
    if (isMacroLevel && fixtureDepartmentMap) {
      for (const a of fixtureAssignments) {
        if (!subcategoryDepartment.has(a.subcategoryId)) {
          const dept = fixtureDepartmentMap.get(a.fixtureId);
          if (dept) subcategoryDepartment.set(a.subcategoryId, dept);
        }
      }
    }

    // Use allocations order as-is — orderedViewAllocations already provides fixture-sequence order
    const entries: AllocationLegendEntry[] = validAllocations.map((alloc) => ({
      abbreviation: isMacroLevel ? alloc.subcategoryName : alloc.subcategoryName.slice(0, 2),
      fullName: isMacroLevel
        ? subcategoryDepartment.get(alloc.subcategoryId) || alloc.subcategoryName
        : alloc.subcategoryName,
      color: alloc.color,
      percentage: alloc.percentage,
      fixtureCount: fixtureCounts.get(alloc.subcategoryId) || 0,
    }));

    return entries;
  }, [allocations, fixtureAssignments, fixtureDepartmentMap]);

  const subcategoryAllocationMode: DisplayMode = useMemo(
    () => ({
      id: 'SUBCATEGORY_ALLOCATION',
      name: 'Subcategory Allocation',
      render: (fixture: BayData) => {
        const assignment = fixtureAssignmentMap.get(fixture.id ?? '');
        if (!assignment) {
          return { displayText: '', fillColor: defaultFillColor };
        }
        // For macro-level assignments (sequenceOrder === 0), show full name; otherwise abbreviate
        const abbrev =
          assignment.sequenceOrder === 0 ? assignment.subcategoryName : assignment.subcategoryName.slice(0, 2);
        if (assignment.isBoundary && assignment.boundaryGradient) {
          const names = (assignment.boundaryNames ?? []).map((n) => n.slice(0, 2));
          const fullNames = (assignment.boundaryNames ?? []).join(' / ');
          return {
            displayText: names.join('/'),
            tooltipText: fullNames,
            fillColor: assignment.color,
            gradient: createWeightedGradient(assignment.boundaryGradient),
          };
        }
        return {
          displayText: abbrev,
          tooltipText: assignment.subcategoryName,
          fillColor: assignment.color,
        };
      },
      legend: allocationLegendEntries.length > 0 ? AllocationLegend({ entries: allocationLegendEntries }) : undefined,
    }),
    [fixtureAssignmentMap, allocationLegendEntries],
  );

  const optionsList = sortBy(
    [
      fixtureIdDisplayMode,
      fixtureModelDisplayMode,
      fixtureTypeDisplayMode,
      departmentMode,
      pickSequenceMode,
      subcategoryAllocationMode,
    ],
    ['name'],
  );

  const defaultOption = subcategoryAllocationMode;

  return {
    optionsList,
    defaultOption,
  };
}

export default useDisplayModes;
